import { Button, Collapse, Grid, IconButton } from '@material-ui/core'
import { ChevronRight } from '@material-ui/icons'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { updateUserInfo } from '../../../actions/userActions'
import { SIDE_NAV_CLOSE } from '../../../constants/navigationConstants'
import locations from '../../../data/locations'
import { useEmailValidator } from '../../../hooks/useEmailValidator'
import { useInput } from '../../../hooks/useInput'
import AutoCompleteSelect from '../../Form/AutoCompleteSelect'
import TextInput from '../../Form/TextInput'
import ToastMessage from '../../ToastMessage/ToastMessage'

const UserInfo = () => {
  const dispatch = useDispatch()

  const [expanded, setExpanded] = useState(true)

  const userLogin = useSelector((state) => state.userLogin)
  const { userInfo } = userLogin

  const userUpdateInfo = useSelector((state) => state.userUpdateInfo)
  const { success: updateUserInfoSuccess } = userUpdateInfo

  const {
    value: firstName,
    setValue: setFirstName,
    bind: bindFirstName,
    error: firstNameError,
    setError: setFirstNameError,
  } = useInput('')
  const {
    value: lastName,
    setValue: setLastName,
    bind: bindLastName,
    error: lastNameError,
    setError: setLastNameError,
  } = useInput('')
  const {
    value: email,
    setValue: setEmail,
    bind: bindEmail,
    error: emailError,
    setError: setEmailError,
  } = useInput('')
  const { value: phone, setValue: setPhone, bind: bindPhone } = useInput('')
  const {
    value: location,
    setValue: setLocation,
    error: locationError,
  } = useInput('')

  const emailIsValid = useEmailValidator(email)
  const [personalInfoIsDirty, setPersonalInfoIsDirty] = useState(false)
  const [userInfoUpdated, setUserInfoUpdated] = useState(false)

  useEffect(() => {
    if (userInfo) {
      setFirstName(userInfo.firstName)
      setLastName(userInfo.lastName)
      setEmail(userInfo.email)
      setPhone(userInfo.phone ? userInfo.phone : '')
      setLocation(userInfo.location ? userInfo.location : '')
    }
  }, [userInfo, setFirstName, setLastName, setEmail, setPhone, setLocation])

  useEffect(() => {
    if (updateUserInfoSuccess) {
      setUserInfoUpdated(true)
      dispatch({ type: SIDE_NAV_CLOSE })
    }
  }, [updateUserInfoSuccess, dispatch])

  useEffect(() => {
    if (userInfo) {
      if (
        firstName !== userInfo.firstName ||
        lastName !== userInfo.lastName ||
        email !== userInfo.email ||
        phone !== userInfo.phone ||
        location !== userInfo.location
      ) {
        setPersonalInfoIsDirty(true)
      } else {
        setPersonalInfoIsDirty(false)
      }
    }
  }, [userInfo, firstName, lastName, email, phone, location])

  const handleExpandClick = () => {
    setExpanded(!expanded)
  }

  const formIsValid = () => {
    let ready = true

    if (firstName === '') {
      ready = false
      setFirstNameError('Please enter your first name')
    } else {
      setFirstNameError('')
    }

    if (lastName === '') {
      ready = false
      setLastNameError('Please enter your last name')
    } else {
      setLastNameError('')
    }

    if (email === '') {
      ready = false
      setEmailError('Please enter your last name')
    } else if (!emailIsValid) {
      ready = false
      setEmailError('Please enter a valid email')
    } else {
      setEmailError('')
    }

    return ready
  }

  const handleUpdate = () => {
    if (formIsValid()) {
      const user = {
        firstName,
        lastName,
        email,
        phone,
        location,
      }

      dispatch(updateUserInfo(user))
    }
  }
  return (
    <div
      style={{ borderBottom: '1px solid black', borderTop: '1px solid black' }}
    >
      <div className="p-t-40 p-b-40">
        <div className="flex space-between">
          <div className="flex">
            <div>
              <IconButton
                onClick={handleExpandClick}
                aria-expanded={expanded}
                aria-label="show more"
                className={`m-r-10 ${expanded ? 'rotate' : ''}`}
              >
                <ChevronRight />
              </IconButton>
            </div>
            <div>
              <h4 className="m-b-0">Personal information</h4>
              <p className="m-b-0 gray-text fs14">
                Update your basic information.
              </p>
            </div>
          </div>
        </div>
        <Collapse in={expanded} timeout="auto" unmountOnExit className="m-l-44">
          <Grid container spacing={2} className="m-t-32">
            <Grid item xs={6}>
              <TextInput
                name="firstName"
                label="First name"
                bindValue={bindFirstName}
                error={firstNameError}
              />
            </Grid>

            <Grid item xs={6}>
              <TextInput
                name="lastName"
                label="Last name"
                bindValue={bindLastName}
                error={lastNameError}
              />
            </Grid>
            <Grid item xs={6}>
              <TextInput
                name="email"
                label="Email"
                bindValue={bindEmail}
                error={emailError}
              />
            </Grid>

            <Grid item xs={6}>
              <TextInput
                type="tel"
                name="phone"
                label="Phone number"
                bindValue={bindPhone}
              />
            </Grid>

            {userInfo.isAdmin && (
              <Grid item xs={6}>
                <AutoCompleteSelect
                  name="location"
                  label="Location"
                  options={locations}
                  value={location}
                  setValue={setLocation}
                  error={locationError}
                  minWidth={558}
                />
              </Grid>
            )}
          </Grid>

          <div className="flex flex-end">
            <Button
              variant="contained"
              className="m-t-30"
              color="primary"
              disabled={!personalInfoIsDirty}
              onClick={handleUpdate}
            >
              Update
            </Button>
          </div>
          <ToastMessage
            message={
              <>
                <p className="fs16 fw600">Account info updated</p>
                <p className="fs14 m-b-0">
                  You have successfully updated your account information.
                </p>
              </>
            }
            open={userInfoUpdated}
            setOpen={setUserInfoUpdated}
          />
        </Collapse>
      </div>
    </div>
  )
}

export default UserInfo
